import { useState, useEffect, useContext } from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import {
  Warehouse,
  MapPin,
  Plus,
  Pencil,
  Power,
  PowerOff,
  ChevronDown,
  ChevronRight,
  X,
  Check,
} from "lucide-react";
import { AuthContext } from "../../../shared/context/auth-context";
import { useLoading } from "../../../shared/context/LoadingContext";
import toast from "react-hot-toast";
import "./ModalGestaoArmazens.scss";

// ─── Formulário inline de armazém ────────────────────────────────────────────
function ArmazemForm({
  inicial,
  clinicaId,
  auth,
  sendRequest,
  onSave,
  onCancel,
  unidades = [],
}) {
  const [nome, setNome] = useState(inicial?.nome || "");
  const [codigo, setCodigo] = useState(inicial?.codigo || "");
  const [saving, setSaving] = useState(false);

  /* A unidade pode vir populada (um objeto) ou como id, conforme o armazém
     tenha acabado de ser gravado ou venha da listagem. */
  const [estabelecimento, setEstabelecimento] = useState(
    inicial?.estabelecimento?._id || inicial?.estabelecimento || "",
  );

  // Com uma unidade só não há escolha a fazer: o servidor atribui-a sozinho.
  const escolheUnidade = unidades.length > 1;

  const submit = async () => {
    if (!nome.trim() || !codigo.trim()) {
      toast.error("Nome e código são obrigatórios.");
      return;
    }

    /* Recusado aqui e no servidor. Aqui para a pessoa perceber logo o que
       falta; lá porque um pedido pode não vir deste ecrã. */
    if (escolheUnidade && !estabelecimento) {
      toast.error("Indique a que unidade pertence este armazém.");
      return;
    }

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("clinica", clinicaId);
      fd.append("nome", nome.trim());
      fd.append("codigo", codigo.trim().toUpperCase());
      fd.append("criadoPor", auth.userId);
      if (escolheUnidade) fd.append("estabelecimento", estabelecimento);

      const url = inicial
        ? `${process.env.REACT_APP_BACKEND_LINK}/api/armazens/${inicial.id}`
        : `${process.env.REACT_APP_BACKEND_LINK}/api/armazens`;
      const method = inicial ? "PATCH" : "POST";

      const res = await sendRequest(url, method, fd, {
        Authorization: "Bearer " + auth.token,
      });

      onSave(res.armazem, !inicial);
      toast.success(inicial ? "Armazém atualizado!" : "Armazém criado!");
    } catch (err) {
      toast.error(err.message || "Erro ao guardar armazém.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mga__inline-form">
      <input
        className="mga__input"
        placeholder="Nome do armazém"
        value={nome}
        onChange={(e) => setNome(e.target.value)}
      />
      <input
        className="mga__input mga__input--sm"
        placeholder="Código (ex: A1)"
        value={codigo}
        onChange={(e) => setCodigo(e.target.value)}
        maxLength={10}
      />
      {escolheUnidade && (
        <select
          className="mga__input mga__input--sm"
          value={estabelecimento}
          onChange={(e) => setEstabelecimento(e.target.value)}
        >
          <option value="">Unidade…</option>
          {unidades.map((u) => (
            <option key={u._id} value={u._id}>
              {u.codigo} — {u.nome}
            </option>
          ))}
        </select>
      )}
      <button
        className="mga__btn mga__btn--save"
        onClick={submit}
        disabled={saving}
      >
        <Check size={14} /> {saving ? "A guardar..." : "Guardar"}
      </button>
      <button className="mga__btn mga__btn--cancel" onClick={onCancel}>
        <X size={14} /> Cancelar
      </button>
    </div>
  );
}

// ─── Formulário inline de localização ────────────────────────────────────────
function LocalizacaoForm({
  inicial,
  armazemId,
  clinicaId,
  auth,
  sendRequest,
  onSave,
  onCancel,
}) {
  const [nome, setNome] = useState(inicial?.nome || "");
  const [codigo, setCodigo] = useState(inicial?.codigo || "");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!nome.trim() || !codigo.trim()) {
      toast.error("Nome e código são obrigatórios.");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("clinica", clinicaId);
      fd.append("armazem", armazemId);
      fd.append("nome", nome.trim());
      fd.append("codigo", codigo.trim().toUpperCase());
      fd.append("criadoPor", auth.userId);

      const url = inicial
        ? `${process.env.REACT_APP_BACKEND_LINK}/api/localizacoes/${inicial.id}`
        : `${process.env.REACT_APP_BACKEND_LINK}/api/localizacoes`;
      const method = inicial ? "PATCH" : "POST";

      const res = await sendRequest(url, method, fd, {
        Authorization: "Bearer " + auth.token,
      });

      onSave(res.localizacao, !inicial);
      toast.success(
        inicial ? "Localização atualizada!" : "Localização criada!",
      );
    } catch (err) {
      toast.error(err.message || "Erro ao guardar localização.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mga__inline-form mga__inline-form--loc">
      <input
        className="mga__input"
        placeholder="Nome da localização (ex: Prateleira 1)"
        value={nome}
        onChange={(e) => setNome(e.target.value)}
      />
      <input
        className="mga__input mga__input--sm"
        placeholder="Código (ex: A1-P1)"
        value={codigo}
        onChange={(e) => setCodigo(e.target.value)}
        maxLength={15}
      />
      <button
        className="mga__btn mga__btn--save"
        onClick={submit}
        disabled={saving}
      >
        <Check size={14} /> {saving ? "A guardar..." : "Guardar"}
      </button>
      <button className="mga__btn mga__btn--cancel" onClick={onCancel}>
        <X size={14} /> Cancelar
      </button>
    </div>
  );
}

// ─── Linha de localização ─────────────────────────────────────────────────────
function LocalizacaoRow({
  loc,
  armazemId,
  clinicaId,
  auth,
  sendRequest,
  onUpdate,
}) {
  const [editando, setEditando] = useState(false);
  const [toggling, setToggling] = useState(false);

  const toggleAtivo = async () => {
    setToggling(true);
    try {
      const rota = loc.ativo ? "desativar" : "reativar";
      const fd = new FormData();
      const res = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/localizacoes/${rota}/${loc.id}`,
        "PATCH",
        fd,
        { Authorization: "Bearer " + auth.token },
      );
      onUpdate(res.localizacao);
      toast.success(
        loc.ativo ? "Localização desativada." : "Localização reativada.",
      );
    } catch (err) {
      toast.error("Erro ao alterar estado.");
    } finally {
      setToggling(false);
    }
  };

  if (editando) {
    return (
      <LocalizacaoForm
        inicial={loc}
        armazemId={armazemId}
        clinicaId={clinicaId}
        auth={auth}
        sendRequest={sendRequest}
        onSave={(updated) => {
          onUpdate(updated);
          setEditando(false);
        }}
        onCancel={() => setEditando(false)}
      />
    );
  }

  return (
    <div
      className={`mga__loc-row ${!loc.ativo ? "mga__loc-row--inativo" : ""}`}
    >
      <MapPin size={13} className="mga__loc-icon" />
      <span className="mga__loc-codigo">{loc.codigo}</span>
      <span className="mga__loc-nome">{loc.nome}</span>
      {!loc.ativo && (
        <span className="mga__badge mga__badge--inativo">Inativo</span>
      )}
      <div className="mga__row-actions">
        <button
          className="mga__icon-btn"
          title="Editar"
          onClick={() => setEditando(true)}
        >
          <Pencil size={13} />
        </button>
        <button
          className={`mga__icon-btn ${loc.ativo ? "mga__icon-btn--danger" : "mga__icon-btn--success"}`}
          title={loc.ativo ? "Desativar" : "Reativar"}
          onClick={toggleAtivo}
          disabled={toggling}
        >
          {loc.ativo ? <PowerOff size={13} /> : <Power size={13} />}
        </button>
      </div>
    </div>
  );
}

// ─── Card de armazém ──────────────────────────────────────────────────────────
function ArmazemCard({
  armazem,
  localizacoes,
  clinicaId,
  auth,
  sendRequest,
  onArmazemUpdate,
  onLocalizacaoSave,
  onLocalizacaoUpdate,
  unidades = [],
}) {
  const [expanded, setExpanded] = useState(true);
  const [editando, setEditando] = useState(false);
  const [adicionandoLoc, setAdicionandoLoc] = useState(false);
  const [toggling, setToggling] = useState(false);

  // Vem populada da listagem; a seguir a gravar pode vir só o id.
  const idDaUnidade =
    armazem.estabelecimento?._id || armazem.estabelecimento || null;
  const unidadeDoArmazem = idDaUnidade
    ? unidades.find((u) => String(u._id) === String(idDaUnidade))
    : null;

  const minhasLocs = localizacoes.filter(
    (l) => l.armazem?.id === armazem.id || l.armazem === armazem.id,
  );

  const toggleAtivo = async () => {
    setToggling(true);
    try {
      const rota = armazem.ativo ? "desativar" : "reativar";
      const fd = new FormData();
      const res = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/armazens/${rota}/${armazem.id}`,
        "PATCH",
        fd,
        { Authorization: "Bearer " + auth.token },
      );
      onArmazemUpdate(res.armazem);
      toast.success(
        armazem.ativo ? "Armazém desativado." : "Armazém reativado.",
      );
    } catch (err) {
      toast.error("Erro ao alterar estado.");
    } finally {
      setToggling(false);
    }
  };

  return (
    <div
      className={`mga__armazem-card ${!armazem.ativo ? "mga__armazem-card--inativo" : ""}`}
    >
      {/* Cabeçalho do armazém */}
      <div className="mga__armazem-header">
        <button
          className="mga__expand-btn"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>
        <Warehouse size={18} className="mga__armazem-icon" />
        <span className="mga__armazem-codigo">{armazem.codigo}</span>
        <span className="mga__armazem-nome">{armazem.nome}</span>
        <span className="mga__armazem-count">
          {minhasLocs.filter((l) => l.ativo).length} loc.
        </span>
        {/* Só nas clínicas com mais do que uma unidade. Numa clínica com uma
            só, dizer a unidade em todas as linhas é ruído. */}
        {unidades.length > 1 && (
          <span
            className={`mga__badge ${unidadeDoArmazem ? "" : "mga__badge--inativo"}`}
            title={
              unidadeDoArmazem
                ? `Armazém da unidade ${unidadeDoArmazem.nome}`
                : "Sem unidade — visível em todas as unidades"
            }
          >
            {unidadeDoArmazem ? unidadeDoArmazem.codigo : "sem unidade"}
          </span>
        )}
        {!armazem.ativo && (
          <span className="mga__badge mga__badge--inativo">Inativo</span>
        )}

        <div className="mga__row-actions">
          <button
            className="mga__icon-btn"
            title="Editar"
            onClick={() => setEditando((v) => !v)}
          >
            <Pencil size={14} />
          </button>
          <button
            className={`mga__icon-btn ${armazem.ativo ? "mga__icon-btn--danger" : "mga__icon-btn--success"}`}
            title={armazem.ativo ? "Desativar" : "Reativar"}
            onClick={toggleAtivo}
            disabled={toggling}
          >
            {armazem.ativo ? <PowerOff size={14} /> : <Power size={14} />}
          </button>
        </div>
      </div>

      {/* Formulário de edição do armazém */}
      {editando && (
        <ArmazemForm
          inicial={armazem}
          clinicaId={clinicaId}
          auth={auth}
          sendRequest={sendRequest}
          onSave={(updated) => {
            onArmazemUpdate(updated);
            setEditando(false);
          }}
          onCancel={() => setEditando(false)}
          unidades={unidades}
        />
      )}

      {/* Localizações */}
      {expanded && (
        <div className="mga__loc-list">
          {minhasLocs.length === 0 && !adicionandoLoc && (
            <p className="mga__empty-locs">Nenhuma localização criada.</p>
          )}

          {minhasLocs.map((loc) => (
            <LocalizacaoRow
              key={loc.id}
              loc={loc}
              armazemId={armazem.id}
              clinicaId={clinicaId}
              auth={auth}
              sendRequest={sendRequest}
              onUpdate={onLocalizacaoUpdate}
            />
          ))}

          {adicionandoLoc ? (
            <LocalizacaoForm
              armazemId={armazem.id}
              clinicaId={clinicaId}
              auth={auth}
              sendRequest={sendRequest}
              onSave={(nova) => {
                onLocalizacaoSave(nova);
                setAdicionandoLoc(false);
              }}
              onCancel={() => setAdicionandoLoc(false)}
            />
          ) : (
            armazem.ativo && (
              <button
                className="mga__btn mga__btn--add-loc"
                onClick={() => setAdicionandoLoc(true)}
              >
                <Plus size={13} /> Adicionar Localização
              </button>
            )
          )}
        </div>
      )}
    </div>
  );
}

// ─── Modal principal ──────────────────────────────────────────────────────────
function ModalGestaoArmazens({ open, handleClose, sendRequest, clinicaId }) {
  const auth = useContext(AuthContext);
  const { startLoading, stopLoading } = useLoading();

  const [armazens, setArmazens] = useState([]);
  const [localizacoes, setLocalizacoes] = useState([]);
  const [adicionandoArmazem, setAdicionandoArmazem] = useState(false);

  /* As unidades da clínica, para se poder dizer a que unidade pertence cada
     armazém. Fica vazia nas clínicas de unidade única — e aí o formulário nem
     mostra o campo: escolher entre uma opção só é trabalho sem sentido, e o
     servidor atribui a única que existe. */
  const [unidades, setUnidades] = useState([]);

  useEffect(() => {
    if (open) fetchTudo();
  }, [open]);

  const fetchTudo = async () => {
    startLoading();
    try {
      const [resArmazens, resLocs] = await Promise.all([
        sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/armazens/clinica/${clinicaId}`,
          "GET",
          null,
          { Authorization: "Bearer " + auth.token },
        ),
        sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/localizacoes/clinica/${clinicaId}`,
          "GET",
          null,
          { Authorization: "Bearer " + auth.token },
        ),
      ]);
      setArmazens(resArmazens.armazens || []);
      setLocalizacoes(resLocs.localizacoes || []);

      /* À parte do Promise.all: numa clínica de unidade única esta chamada não
         é precisa, e uma falha aqui não deve impedir a lista de armazéns de
         aparecer — a gestão de armazéns tem de funcionar na mesma. */
      try {
        const resUnidades = await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/estabelecimentos/clinica/${clinicaId}`,
          "GET",
          null,
          { Authorization: "Bearer " + auth.token },
        );
        setUnidades(resUnidades.estabelecimentos || []);
      } catch (err) {
        console.error("Erro ao carregar unidades:", err);
      }
    } catch (err) {
      toast.error("Erro ao carregar armazéns.");
    } finally {
      stopLoading();
    }
  };

  // Armazéns
  const handleArmazemSave = (novo, isNew) => {
    if (isNew) {
      setArmazens((prev) => [...prev, novo]);
      setAdicionandoArmazem(false);
    } else {
      setArmazens((prev) => prev.map((a) => (a.id === novo.id ? novo : a)));
    }
  };

  const handleArmazemUpdate = (updated) => {
    setArmazens((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
  };

  // Localizações
  const handleLocalizacaoSave = (nova) => {
    setLocalizacoes((prev) => [...prev, nova]);
  };

  const handleLocalizacaoUpdate = (updated) => {
    setLocalizacoes((prev) =>
      prev.map((l) => (l.id === updated.id ? updated : l)),
    );
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box className="fluxo-atendimento__modal__box mga__box">
        {/* Título */}
        <div className="mga__header">
          <div className="mga__header__left">
            <Warehouse size={22} className="mga__header__icon" />
            <h1 className="fluxo-atendimento__modal__titulo">
              Gestão de Armazéns e Localizações
            </h1>
          </div>
          <button
            className="mga__icon-btn mga__close-btn"
            onClick={handleClose}
          >
            <X size={20} />
          </button>
        </div>

        {/* Corpo */}
        <div className="mga__body">
          {armazens.length === 0 && !adicionandoArmazem && (
            <p className="mga__empty">
              Nenhum armazém criado. Crie o primeiro abaixo.
            </p>
          )}

          {armazens.map((arm) => (
            <ArmazemCard
              key={arm.id}
              armazem={arm}
              localizacoes={localizacoes}
              clinicaId={clinicaId}
              auth={auth}
              sendRequest={sendRequest}
              onArmazemUpdate={handleArmazemUpdate}
              onLocalizacaoSave={handleLocalizacaoSave}
              onLocalizacaoUpdate={handleLocalizacaoUpdate}
              unidades={unidades}
            />
          ))}

          {adicionandoArmazem ? (
            <ArmazemForm
              clinicaId={clinicaId}
              auth={auth}
              sendRequest={sendRequest}
              onSave={handleArmazemSave}
              onCancel={() => setAdicionandoArmazem(false)}
              unidades={unidades}
            />
          ) : (
            <button
              className="mga__btn mga__btn--add-arm"
              onClick={() => setAdicionandoArmazem(true)}
            >
              <Plus size={15} /> Novo Armazém
            </button>
          )}
        </div>

        <div className="fluxo-atendimento__modal__bottom_btns">
          <span className="cancel-btn" onClick={handleClose}>
            Fechar
          </span>
        </div>
      </Box>
    </Modal>
  );
}

export default ModalGestaoArmazens;
